import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getAccountExtensionScanResult,
  getExtensionUsers,
  getOrgExtensionScanResult,
} from '@kirin/api-client/api/extensions';
import type { ExtensionScanResultDetail } from '@kirin/api-client/contracts/extensions';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

/**
 * Hook to fetch users who have a specific extension installed
 *
 * Authorization:
 * - Owner/Manager: Returns all users with this extension
 * - Member: Returns only the requesting user's data
 *
 * @param organizationId - Organization ID (required)
 * @param extensionId - Extension ID (required)
 * @param enabled - Whether to enable the query
 *
 * @example
 * const { data, isLoading, error } = useExtensionUsers(orgId, 'microsoft.python');
 *
 * @returns React Query result with users list
 */
export function useExtensionUsers(
  organizationId: string | undefined,
  extensionId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.extensions.users(organizationId, extensionId),
    queryFn: () => {
      if (!organizationId || !extensionId) {
        throw new Error('organizationId and extensionId are required');
      }
      return getExtensionUsers(apiClient, organizationId, extensionId);
    },
    enabled: enabled && !!organizationId && !!extensionId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}

export function useExtensionScanResult(
  organizationId: string | undefined,
  extensionId: string | undefined,
  version: string | undefined
): UseQueryResult<ExtensionScanResultDetail> {
  return useQuery<ExtensionScanResultDetail>({
    queryKey: queryKeys.extensions.scanResult(
      organizationId,
      extensionId,
      version
    ),
    queryFn: ({ signal }) => {
      if (!extensionId) {
        throw new Error('extensionId is required');
      }

      if (organizationId) {
        return getOrgExtensionScanResult(
          apiClient,
          organizationId,
          extensionId,
          { version },
          { signal }
        );
      }

      return getAccountExtensionScanResult(
        apiClient,
        extensionId,
        { version },
        { signal }
      );
    },
    enabled: Boolean(extensionId),
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}
